import type {
  MapDefinition,
  NpcDifficulty,
  RaceEventMode,
  TimeOfDay,
  Weather,
} from '../shared/types.js';

export interface QuickEvent {
  map: MapDefinition;
  eventMode: Exclude<RaceEventMode, 'time-trial'>;
  timeOfDay: TimeOfDay;
  weather: Weather;
  traffic: boolean;
  npcCount: number;
  npcDifficulty: NpcDifficulty;
}

const MODES: readonly QuickEvent['eventMode'][] = [
  'circuit',
  'sprint',
  'style-rush',
  'elimination',
  'heat-run',
  'road-rage',
];
const TIMES: readonly TimeOfDay[] = ['dawn', 'day', 'sunset', 'night'];
const WEATHER: readonly Weather[] = ['clear', 'haze', 'rain', 'sand'];

/** Deterministic for a supplied seed so tests, offline rooms and a visible
 * "next event" button always agree on the selected rules. */
export function quickEventFor(
  maps: readonly MapDefinition[],
  seed: string,
  currentMapId: string,
  npcDifficulty: NpcDifficulty,
): QuickEvent | null {
  if (maps.length === 0) return null;
  const first = hash(seed);
  const candidates = maps.filter((map) => map.id !== currentMapId);
  const pool = candidates.length > 0 ? candidates : [...maps];
  const map = pool[first % pool.length]!;
  const eventMode = MODES[hash(`${seed}:mode`) % MODES.length]!;
  const authoredTimeChance = hash(`${seed}:time:authored`) % 3 === 0;
  const authoredWeatherChance = hash(`${seed}:weather:authored`) % 3 === 0;
  const timeOfDay = authoredTimeChance
    ? map.timeOfDay
    : TIMES[hash(`${seed}:time`) % TIMES.length]!;
  let weather = authoredWeatherChance
    ? map.weather
    : WEATHER[hash(`${seed}:weather`) % WEATHER.length]!;
  if (map.biome === 'alpine' && weather === 'sand') weather = 'haze';
  return {
    map,
    eventMode,
    timeOfDay,
    weather,
    traffic: eventMode !== 'style-rush' && hash(`${seed}:traffic`) % 4 === 0,
    npcCount: eventMode === 'elimination' ? 7 : 5,
    npcDifficulty,
  };
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}
